/**
 * @module LibraryPanelRow
 * @purpose One compact row in the hypothesis library side-list: display label, role chip,
 *   factual coverage ratio, and a neutral status token. No card, no walkthrough, no score.
 * @depends coverage, types/reasoning, react-i18next
 * @dependedOnBy LibraryPanel
 *
 * @rules
 * - Performance: pure display — all data via props, no data fetching
 * - State: none
 * - INVARIANT: Evidenx does not score — copy is a factual inventory only.
 */

import { useTranslation } from 'react-i18next'
import type { Hypothesis } from '../../../../../types/reasoning'
import { computeCoverage, coverageStatus } from './coverage'

interface LibraryPanelRowProps {
  hypothesis: Hypothesis
}

const ROW_STYLE: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: '8px',
  padding: '8px 12px',
  borderTop: '1px solid var(--border)',
}

const LABEL_STYLE: React.CSSProperties = {
  fontFamily: 'var(--font-display)',
  fontSize: '0.78rem',
  color: 'var(--text)',
  fontWeight: 600,
}

const CHIP_BASE: React.CSSProperties = {
  fontFamily: 'var(--font-mono)',
  fontSize: '0.58rem',
  padding: '1px 6px',
  borderRadius: '4px',
  display: 'inline-block',
  border: '1px solid var(--border)',
  color: 'var(--text-muted)',
  background: 'transparent',
}

const RATIO_STYLE: React.CSSProperties = {
  fontFamily: 'var(--font-mono)',
  fontSize: '0.68rem',
  color: 'var(--text-dim)',
  marginLeft: 'auto',
}

const STATUS_STYLE: React.CSSProperties = {
  fontFamily: 'var(--font-mono)',
  fontSize: '0.6rem',
  color: 'var(--text-muted)',
}

const STATUS_KEYS = {
  no_indicators: 'reasoning.hypothesis.library.statusNoIndicators',
  required_absent: 'reasoning.hypothesis.library.statusRequiredAbsent',
  partial: 'reasoning.hypothesis.library.statusPartial',
} as const

/**
 * A single compact library row.
 *
 * @behaviour Shows the predicate name (mirrors HypothesisCard, which renders predicateName
 *   directly), the role, the "{present} of {declared} indicators observed" ratio, and a
 *   factual status token. Non-interactive in v1 (click-to-expand deferred).
 * @errors n/a
 */
export function LibraryPanelRow({ hypothesis }: LibraryPanelRowProps): React.ReactElement {
  const { t } = useTranslation('analysis')
  const coverage = computeCoverage(hypothesis)
  const status = coverageStatus(hypothesis)
  const roleLabel = hypothesis.role === 'source'
    ? t('reasoning.hypothesis.card.roleSource')
    : hypothesis.role === 'target'
      ? t('reasoning.hypothesis.card.roleTarget')
      : t('reasoning.hypothesis.card.roleUnspecified')

  return (
    <div style={ROW_STYLE}>
      <span style={LABEL_STYLE}>{hypothesis.predicateName}</span>
      <span style={CHIP_BASE}>{roleLabel}</span>
      <span style={STATUS_STYLE}>{t(STATUS_KEYS[status])}</span>
      <span style={RATIO_STYLE}>
        {t('reasoning.hypothesis.library.coverageRatio', {
          present: coverage.present,
          declared: coverage.declared,
        })}
      </span>
    </div>
  )
}
